import React, { useState, useCallback, useRef, useEffect, useMemo, Fragment } from 'react'
import { Button, CustomAnchor, InputText, PopoverThreeDots } from '../../components'
import ChatItem from './chat-item';
import References from './references';
import { askDocsAPI, getAxiosCancelTokenSource, getChatHistoryAPI, isCanceledRequestError } from '../../common/services';
import PageLoader from '../page-loader';
import { useScrollContext } from '../../common/contexts/scroll';
import { SCROLL_REFERENCE_KEYS } from '../../constants';
import { organizationState } from '../../atoms';
import { useRecoilValue } from 'recoil';

const Chat = ({
    id,
    type: chatType = "organization",
    size = "medium", // options: medium, large
    withReferences = false,
    headerContent,
    suggestedQuestions,
    objectId,
    houseId,
    showToaster,
    onAskQuestion,
    onResetChat,
    onReferenceClick,
    onRefrenceChange,
    placeholder = "Ask a question...",
    onClickDelete
}) => {

    // local states
    const [chatId, setChatId] = useState()
    const [chatHistory, setChatHistory] = useState();
    const [currentQuestion, setCurrentQuestion] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [pageLoader, setPageLoader] = useState(false);
    const [references, setReferences] = useState([]);

    // global states
    const { id: orgId } = useRecoilValue(organizationState);

    //ref
    const inputRef = useRef(null);
    const chatListRef = useRef(null);
    const uniqueId = useRef(Date.now())
    const axiosCancelTokenSource = useRef(null)
    const validateObjectId = useRef()
    const { registerRef } = useScrollContext()

    // memo
    const defaultChatId = useMemo(() => `${houseId || orgId}_${uniqueId.current}`, [houseId, orgId])
    const showNewChatBtn = useMemo(() => !!chatHistory?.length && !isLoading, [chatHistory, isLoading])

    const resetChat = useCallback(() => {
        // this will reset the chatId and chatHistory
        setChatId(defaultChatId)
        setChatHistory([]);
        setCurrentQuestion('');
        setReferences([]);
        onResetChat && onResetChat()
    }, [onResetChat, defaultChatId],);

    const handleNewChat = useCallback(() => {
        resetChat()
        uniqueId.current = Date.now()
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [resetChat]);

    const handleInputChange = useCallback((value) => {
        setCurrentQuestion(value);
    }, []);

    const handleAskQuestion = useCallback(async (suggestedQuestion) => {
        let questionValue = currentQuestion.trim();
        setIsLoading(true);
        if (suggestedQuestion && (currentQuestion !== suggestedQuestion)) {
            handleInputChange(suggestedQuestion)
            questionValue = suggestedQuestion;
        } else if (!questionValue) {
            return;
        }

        const newQuestion = {
            id: Date.now().toString(),
            question: questionValue,
            response: '',
            isLiked: false,
            isDisliked: false,
            status: "loading"
        };

        // Add question to chat list
        setChatHistory(prev => [...(prev || []), newQuestion]);
        setCurrentQuestion('');
        onAskQuestion && onAskQuestion(questionValue)

        let askDocsAPIData = {
            question: questionValue,
            chatId,
            type: chatType
        }

        if (houseId) {
            askDocsAPIData.houseId = houseId
        }

        if (objectId) {
            askDocsAPIData.objectId = objectId?.toString()
        }

        try {
            axiosCancelTokenSource.current = getAxiosCancelTokenSource()
            const askDocsResponse = await askDocsAPI(askDocsAPIData, axiosCancelTokenSource.current.token)
            const answer = askDocsResponse.data.answer
            const responseReferences = askDocsResponse?.data?.docHighlightInfo

            if (withReferences && responseReferences) {
                setReferences(responseReferences);
            } else if (responseReferences && onRefrenceChange) {
                onRefrenceChange(responseReferences)
            }

            setChatHistory(prev => prev.map(chat => chat.id === newQuestion.id ? ({ ...chat, response: answer, status: "completed" }) : chat))
        } catch (error) {
            if (!isCanceledRequestError(error)) {
                console.log("Error while asking question", error);
                setChatHistory(prev => prev.map(chat => chat.id === newQuestion.id ? { ...chat, status: "error" } : chat))
            }
        } finally {
            setIsLoading(false);
        }

    }, [currentQuestion, onAskQuestion, chatId, chatType, houseId, objectId, handleInputChange, withReferences, onRefrenceChange]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAskQuestion();
        }
    }, [handleAskQuestion]);

    const onRetryAskQuestion = useCallback((chatData) => {
        // remove old question
        setChatHistory(oldChats => oldChats.filter(chat => chat.id !== chatData.id))
        // ask question again
        handleAskQuestion(chatData.question)
    }, [handleAskQuestion])

    const handleUpdateChat = useCallback((chatId, updates) => {
        setChatHistory(prev =>
            prev.map(chat => ((chat.id === chatId) ? { ...chat, ...updates } : chat))
        );
    }, []);

    // this will load the past chat history
    const loadPastChat = useCallback(async (id) => {
        try {
            const chatHistoryResponse = await getChatHistoryAPI({ chatId: id })
            const allChats = chatHistoryResponse.data.chat;

            setChatHistory(formatChatHistory(allChats))
            const lastChatRefrences = (allChats || []).pop()?.docHighlightInfo || [];

            if (withReferences) {
                setReferences(lastChatRefrences)
            }

            if (onRefrenceChange) {
                onRefrenceChange(lastChatRefrences)
            }

            // scroll to last
            if (chatListRef.current) {
                chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
            }
        } catch (error) {
            console.log("Error while fetching Chat History", error);
        } finally {
            setPageLoader(false)
        }
    }, [withReferences, onRefrenceChange])

    useEffect(() => {
        // this will change the chatId when objectId changes
        if (objectId) {
            const newChatId = `${houseId}_${objectId}_${uniqueId.current}`
            setChatId(newChatId)
            loadPastChat(newChatId)

        } else {
            resetChat()
        }

    }, [houseId, objectId, loadPastChat, resetChat])

    useEffect(() => {
        registerRef(SCROLL_REFERENCE_KEYS.chatList, chatListRef.current)
        return () => registerRef(SCROLL_REFERENCE_KEYS.chatList, null);
    }, [registerRef, chatHistory])

    useEffect(() => {
        // this will open the chat when id is passed from outside
        if (id) {
            setChatId(id)
            setPageLoader(true)
            loadPastChat(id)
        }
    }, [id, loadPastChat])

    //this is solution of canceling api call when unselect/chaging of element
    useEffect(() => {
        if (isLoading && (objectId !== validateObjectId?.current)) {
            validateObjectId.current = null
            axiosCancelTokenSource.current.cancel('Operation canceled by the user.');
        } else {
            validateObjectId.current = objectId
        }
    }, [isLoading, objectId, axiosCancelTokenSource])



    // to open pdf from refrence link which is inside chat
    useEffect(() => {
        const elementsArr = document.getElementsByClassName("ref-link-chat")

        const toOpenPdf = (event) => {
            const dataset = event.target.dataset            
            onReferenceClick({ pdfFileName: dataset.file, pdfPageNumber: dataset.page , pdfFile: { Bucket: dataset.bucket, Key: decodeURIComponent(dataset.key) } })
        }

        if (elementsArr?.length) {
            [...elementsArr].forEach(ele => {
                ele.addEventListener("click", toOpenPdf)
            })
        }

        return () => {
            if (elementsArr?.length) {
                [...elementsArr].forEach(ele => {
                    ele.removeEventListener("click", toOpenPdf)
                })
            }
        }

    }, [chatHistory, onReferenceClick])


    const onDelete = useCallback(() => {
        if (onClickDelete) {
            onClickDelete(resetChat)
        } 
    }, [onClickDelete, resetChat])

    // loader enabled when clicked on any past chat
    if (!chatHistory?.length && pageLoader) {
        return <PageLoader />;
    }

    return (
        <>
            <div className={`bimmatch-chat-container chat-size-${size} ${showNewChatBtn ? "chat-started" : ""}`}>
                <div className='new-chat-container'>
                    <div className='header-actions'>
                        {(showNewChatBtn) && (
                            <div className='new-chat-action'>
                                <CustomAnchor onClick={handleNewChat}>New chat</CustomAnchor>
                                <PopoverThreeDots options={[{ id: "delete", src: "/images/icon/trash.svg", label: "Delete", onClick: onDelete }]} className={'chat-popover'}/>
                            </div>
                        )}
                    </div>
                </div>
                {showNewChatBtn && <hr className='separator' />}
                {(objectId && headerContent) && (
                    <div className='chat-header'>
                        <div className='docs-chat-header-container'>
                            {headerContent && headerContent}
                        </div>
                    </div>
                )}
                {!!chatHistory?.length && (
                    <div className={`chat-list-wrapper`} ref={chatListRef}>
                        {(chatHistory || []).map(chatData => (
                            <Fragment key={chatData.id}>
                                <ChatItem
                                    key={chatData.id}
                                    chatData={chatData}
                                    onUpdateChat={handleUpdateChat}
                                    showToaster={showToaster}
                                    onRetry={() => onRetryAskQuestion(chatData)}
                                />
                                <hr className='separator chat-item-separator' />
                            </Fragment>
                        ))}
                    </div>
                )}
                <div className='ask-input-wrapper'>
                    <InputText
                        ref={inputRef}
                        placeholder={placeholder}
                        hideLabel
                        value={currentQuestion}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                    />
                    <Button
                        type="secondary"
                        onClick={() => handleAskQuestion(false)}
                        disable={!currentQuestion.trim() || isLoading}
                        loading={isLoading}
                    >
                        <img src='/images/icon/brain-ai.svg' alt="Brain" className='ask-icon' />
                        <p>Ask</p>
                    </Button>
                </div>
                {!chatHistory?.length && !!suggestedQuestions?.length && (
                    <div className='suggested-questions-wrapper'>
                        <p className='suggested-questions-title'>Suggested questions</p>
                        <div className='suggested-questions-list'>
                            {suggestedQuestions.map((question, index) => (
                                <CustomAnchor
                                    key={index}
                                    onClick={() => {
                                        handleAskQuestion(question);
                                    }}
                                >
                                    {question}
                                </CustomAnchor>
                            ))}
                        </div>
                        <hr className='separator' />
                    </div>
                )}
                {(withReferences && !!references?.length) && (
                    <div className={`references-wrapper ${isLoading ? "loading" : "loaded"}`}>
                        <References
                            references={references}
                            loading={isLoading}
                            showToaster={showToaster}
                            onReferenceClick={onReferenceClick}
                        />
                    </div>
                )}
            </div>
        </>
    )
}


// NOte: This is a temporary solution to format as api response will change
function formatChatHistory(apiData) {
    let formattedChat = []

    apiData.forEach((chatData, chatIndex) => {
        if (chatData.sender === "user") {
            formattedChat.push({
                id: `question-${chatIndex}`,
                question: chatData.text,
                response: ''
            })
        } else {
            formattedChat[formattedChat.length - 1].response = chatData.text
        }
    });
    return formattedChat
}

export default Chat